import { fmt, hasTermed } from '@shared/lib';
import type { OpsGroup } from '../model/types';

/**
 * The one place that decides what state a group is in.
 *
 * THE RULE: termed is a dated FACT, archived is a SHELF. They are separate
 * fields here on purpose, because collapsing them is the bug this exists to
 * fix. Before this helper, "this group left us" had exactly one representation
 * in the app - somebody unticking vba_isactive - which is undated, has no
 * reason attached, and silently moves reporting denominators (Operation
 * Daylight scoring, Catalog partner-ID coverage and Group Documents coverage
 * all filter `vba_isactive eq true`). Meanwhile the real fact, the CRM
 * account's dts_termdate, was displayed on exactly one page.
 *
 * So: `status` comes from the term date and nothing else. `archived` comes
 * from vba_isactive and nothing else. Anything asking "should I show this in
 * the default list" wants `offBook`. Anything asking a time-aware reporting
 * question ("was this group in force during 2025") must NOT use any of these -
 * it wants the dates, because the shelf flag reflects today's housekeeping,
 * not history.
 *
 * See docs/design/group-lifecycle/TERMED-GROUPS.md.
 */

export type GroupLifecycleStatus = 'active' | 'terming' | 'termed';

export interface GroupLifecycle {
  /** The fact, from the CRM term date. */
  status: GroupLifecycleStatus;
  /** The shelf, from vba_isactive. Independent of status on purpose. */
  archived: boolean;
  /** Term date as YYYY-MM-DD, when CRM has one. */
  termDate?: string;
  /** Badge text ("Termed 12/31/25", "Terming 3/31/26", "Archived"), or null
   *  when the group is plain active and there is nothing worth badging. */
  label: string | null;
  /** Out of the default working book: termed or archived, either one. */
  offBook: boolean;
  /** Archived with no term date in CRM. Nobody recorded when they left, so
   *  "when did we lose them" is now unanswerable from the data. Powers the
   *  Archive dialog's warning. */
  undocumented: boolean;
}

export function groupLifecycle(
  group: Pick<OpsGroup, 'isActive' | 'termDate'>,
): GroupLifecycle {
  const termDate = group.termDate ? group.termDate.split('T')[0] : undefined;
  const archived = !group.isActive;

  // hasTermed (@shared/lib/termedGroup) is the one predicate, local-time by
  // design: a date-only business fact flips when the reader's day passes,
  // not at midnight UTC.
  let status: GroupLifecycleStatus = 'active';
  if (termDate) status = hasTermed(termDate) ? 'termed' : 'terming';

  const label =
    status === 'termed'
      ? `Termed ${fmt.dateCompact(termDate!)}`
      : status === 'terming'
        ? `Terming ${fmt.dateCompact(termDate!)}`
        : archived
          ? 'Archived'
          : null;

  return {
    status,
    archived,
    termDate,
    label,
    offBook: status === 'termed' || archived,
    undocumented: archived && !termDate,
  };
}

/** Badge colour for the lifecycle label. Terming is the one that earns money -
 *  it is the renewal-save window, not a failure - so it reads as a warning
 *  rather than a loss. */
export function lifecycleBadgeColor(
  lc: GroupLifecycle,
): 'danger' | 'warning' | 'informative' {
  if (lc.status === 'termed') return 'danger';
  if (lc.status === 'terming') return 'warning';
  return 'informative';
}
